import { SortType } from '@/types/components';

export const residentSortTypes: SortType[] = [
  'unit', 'notification', 'careTiming', 'notificationAndCareTiming', 'createdAt',
  'name', 'bed', 'daysWithoutDefecation', 'dfreeLatestUsage',
];

export const availableResidentSortTypes = (medical: boolean) => residentSortTypes.filter((type) =>
  !medical || !['careTiming', 'notificationAndCareTiming', 'daysWithoutDefecation'].includes(type));

export const normalizeResidentSortType = (value: unknown, medical = false): SortType =>
  availableResidentSortTypes(medical).includes(value as SortType) ? value as SortType : 'unit';

export interface ResidentSortData {
  id: number;
  name: string;
  nursingHomeUnitName: string;
  roomName: string;
  bed: string | null;
  gasDaysOfNoDefecation: number | null;
  latestUrineVolumeCalculationTimestamp: string | null;
  coverTimestamp: string | null;
  nextCareTiming: string | null;
  inCareTiming: number | null;
  isCareCompleted: number | null;
}

export interface WithResidentSortData {
  sortData?: ResidentSortData;
}

type SortableResident = WithResidentSortData & {
  id?: number;
  residentId?: number;
  name?: string;
  residentName?: string;
  nursingHomeUnitName?: string | null;
  roomName?: string | null;
  bed?: string | null;
  gasDaysOfNoDefecation?: number | null;
  latestUrineVolumeCalculationTimestamp?: string | null;
  coverTimestamp?: string | null;
  nextCareTiming?: string | null;
  inCareTiming?: number | null;
  isCareCompleted?: number | null;
};

const textCompare = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;
const unitCompare = (a: ResidentSortData, b: ResidentSortData): number =>
  textCompare(`${a.nursingHomeUnitName} ${a.roomName}`, `${b.nursingHomeUnitName} ${b.roomName}`)
  || textCompare(a.name, b.name) || a.id - b.id;
const timestamp = (value: string | null): number | null => {
  if (value === null) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const nullableNumberCompare = (a: number | null, b: number | null, descending = false) =>
  a === null ? (b === null ? 0 : 1) : b === null ? -1 : descending ? b - a : a - b;
const carePriority = (resident: ResidentSortData): number => {
  if (timestamp(resident.nextCareTiming) === null || resident.inCareTiming === null || resident.isCareCompleted === null) return 3;
  if (resident.isCareCompleted === 1) return 2;
  if (resident.isCareCompleted === 0 && resident.inCareTiming === 1) return 0;
  if (resident.isCareCompleted === 0 && resident.inCareTiming === 0) return 1;
  return 3;
};
const careTimeCompare = (a: ResidentSortData, b: ResidentSortData): number =>
  nullableNumberCompare(timestamp(a.nextCareTiming), timestamp(b.nextCareTiming)) || unitCompare(a, b);
const careCompare = (a: ResidentSortData, b: ResidentSortData): number => {
  const priority = carePriority(a) - carePriority(b);
  return priority || (carePriority(a) === 3 ? unitCompare(a, b) : careTimeCompare(a, b));
};

// 数字は桁数と文字列で比較し、長いベッド番号でも丸め誤差を生じさせない。
export const compareBed = (left: string | null, right: string | null): number => {
  const a = (left ?? '').trim();
  const b = (right ?? '').trim();
  if (!a || !b) return !a ? (!b ? 0 : 1) : -1;
  const tokensA = a.match(/\d+|[^\d]+/g) ?? [];
  const tokensB = b.match(/\d+|[^\d]+/g) ?? [];
  for (let i = 0; i < Math.min(tokensA.length, tokensB.length); i += 1) {
    const numberA = /^\d+$/.test(tokensA[i]);
    const numberB = /^\d+$/.test(tokensB[i]);
    let result: number;
    if (numberA && numberB) {
      const x = tokensA[i].replace(/^0+/, '') || '0';
      const y = tokensB[i].replace(/^0+/, '') || '0';
      result = x.length - y.length || textCompare(x, y);
    } else if (numberA !== numberB) result = numberA ? -1 : 1;
    else result = textCompare(tokensA[i].toLowerCase(), tokensB[i].toLowerCase());
    if (result) return result;
  }
  return tokensA.length - tokensB.length;
};

const getSortData = (row: SortableResident): ResidentSortData => row.sortData ?? {
  id: row.id ?? row.residentId ?? 0,
  name: row.residentName ?? row.name ?? '',
  nursingHomeUnitName: row.nursingHomeUnitName ?? '',
  roomName: row.roomName ?? '',
  bed: row.bed ?? null,
  gasDaysOfNoDefecation: row.gasDaysOfNoDefecation ?? null,
  latestUrineVolumeCalculationTimestamp: row.latestUrineVolumeCalculationTimestamp ?? null,
  coverTimestamp: row.coverTimestamp ?? null,
  nextCareTiming: row.nextCareTiming ?? null,
  inCareTiming: row.inCareTiming ?? null,
  isCareCompleted: row.isCareCompleted ?? null,
};

export const sortResidentList = <T extends SortableResident>(rows: readonly T[], type: SortType): T[] => {
  const compare = (a: ResidentSortData, b: ResidentSortData): number => {
    switch (type) {
      case 'name': return textCompare(a.name, b.name) || unitCompare(a, b);
      case 'createdAt': return b.id - a.id;
      case 'bed': return compareBed(a.bed, b.bed) || textCompare(a.name, b.name) || a.id - b.id;
      case 'daysWithoutDefecation': return nullableNumberCompare(a.gasDaysOfNoDefecation, b.gasDaysOfNoDefecation, true) || unitCompare(a, b);
      case 'dfreeLatestUsage': return nullableNumberCompare(timestamp(a.latestUrineVolumeCalculationTimestamp), timestamp(b.latestUrineVolumeCalculationTimestamp), true) || unitCompare(a, b);
      case 'notification': return nullableNumberCompare(timestamp(a.coverTimestamp), timestamp(b.coverTimestamp)) || unitCompare(a, b);
      case 'careTiming': return careCompare(a, b);
      case 'notificationAndCareTiming': {
        const notification = nullableNumberCompare(timestamp(a.coverTimestamp), timestamp(b.coverTimestamp));
        if (notification) return notification;
        return timestamp(a.coverTimestamp) !== null ? careTimeCompare(a, b) : careCompare(a, b);
      }
      default: return unitCompare(a, b);
    }
  };
  return rows.map((row) => ({ row, data: getSortData(row) }))
    .sort((a, b) => compare(a.data, b.data)).map(({ row }) => row);
};
